import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Lightbulb, User } from 'lucide-react';

export const SmartFeaturesPage: React.FC = () => {
  return (
    <DocsLayout
      title='Smart Features'
      description='How Vehix helps you work smarter'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Legal', path: '/docs/legal' },
        { label: 'Smart Features' }
      ]}
    >
      <div className='max-w-[1000px]'>
        <p className='text-sm text-muted-foreground italic mb-8'>Last Updated: January 28, 2026</p>

        <div className='mb-8'>
          <Card className='border-primary/50 bg-primary/5'>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <Lightbulb className='h-5 w-5 text-primary' />
                <CardTitle className='text-lg'>What This Page Explains</CardTitle>
              </div>
            </CardHeader>
            <CardContent className='text-sm leading-6'>
              <p>
                Vehix includes smart features that help you work more efficiently. This page
                explains what these features do, how they help you, and how you stay in control.
              </p>
            </CardContent>
          </Card>
        </div>

        <section className='mb-20' id='what-smart-features-do'>
          <h2 className='text-3xl font-bold mb-6'>1. What Smart Features Do</h2>
          <p className='text-sm leading-6 mb-4'>
            Vehix helps you save time by making suggestions for your daily work:
          </p>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>Finding Efficient Routes</h3>
            <p className='text-sm leading-6'>
              When you plan technician routes, Vehix suggests an order of stops that minimizes
              travel time. Instead of figuring out the best order yourself, Vehix does the math and
              shows you a suggested route.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>Matching Technicians to Jobs</h3>
            <p className='text-sm leading-6'>
              When assigning recalls to technicians, Vehix suggests which technician might be the
              best fit. It considers things like the technician skill level and how close they are
              to the dealer location.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>Organizing Your Work</h3>
            <p className='text-sm leading-6'>
              Vehix helps organize recalls by location and priority so you can see what needs
              attention first. This helps you manage your workload without having to sort through
              everything manually.
            </p>
          </div>
        </section>

        <section className='mb-20' id='you-make-decisions'>
          <h2 className='text-3xl font-bold mb-6'>2. You Always Make the Final Decision</h2>

          <div className='mb-6'>
            <Card className='border-green-500/50 bg-green-500/5'>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <User className='h-5 w-5 text-green-600' />
                  <CardTitle className='text-lg text-green-700 dark:text-green-400'>
                    You Are in Control
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <p>
                  Every suggestion Vehix makes is just that - a suggestion. You review it, you
                  decide if it makes sense, and you approve it. Nothing happens automatically
                  without your approval.
                </p>
              </CardContent>
            </Card>
          </div>

          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Review suggestions:</strong> You see what Vehix suggests before anything
              happens
            </li>
            <li className='text-sm leading-6'>
              <strong>Change anything:</strong> You can modify any suggestion to fit your needs
            </li>
            <li className='text-sm leading-6'>
              <strong>Override completely:</strong> You can ignore suggestions and do things your
              way
            </li>
            <li className='text-sm leading-6'>
              <strong>Use your judgment:</strong> You know your business better than any system
            </li>
          </ul>
        </section>

        <section className='mb-20' id='suggestions-not-perfect'>
          <h2 className='text-3xl font-bold mb-6'>3. Suggestions Are Not Perfect</h2>
          <p className='text-sm leading-6 mb-4'>
            While Vehix tries to make helpful suggestions, they may not always be right for your
            situation:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              Suggestions are based only on information in the system
            </li>
            <li className='text-sm leading-6'>
              Vehix does not know about things like traffic, weather, or last-minute changes
            </li>
            <li className='text-sm leading-6'>
              What works well on paper might not work in real life
            </li>
            <li className='text-sm leading-6'>
              Your experience and judgment are valuable - use them
            </li>
          </ul>
          <p className='text-sm leading-6 mt-4'>
            <strong>Always review suggestions before acting on them.</strong> If something does not
            look right, trust your instincts and make adjustments.
          </p>
        </section>

        <section className='mb-20' id='your-data'>
          <h2 className='text-3xl font-bold mb-6'>4. How Your Data Is Used</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>4.1 What We Use</h3>
            <p className='text-sm leading-6 mb-4'>
              To make suggestions, Vehix uses information you have already entered:
            </p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>Dealer locations</li>
              <li className='text-sm leading-6'>Recall information</li>
              <li className='text-sm leading-6'>Technician skills and locations</li>
              <li className='text-sm leading-6'>Your organization settings</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>4.2 What We Do Not Do</h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>
                <strong>We do not sell your data</strong> - Your information stays with us
              </li>
              <li className='text-sm leading-6'>
                <strong>We do not train AI on your data</strong> - Your data is not used to teach
                computer systems
              </li>
              <li className='text-sm leading-6'>
                <strong>We do not share with competitors</strong> - Your business information is
                confidential
              </li>
            </ul>
          </div>
        </section>

        <section className='mb-20' id='your-responsibility'>
          <h2 className='text-3xl font-bold mb-6'>5. Your Responsibility</h2>
          <p className='text-sm leading-6 mb-4'>
            Because you make all final decisions, you are responsible for:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Reviewing all suggestions before approving them</li>
            <li className='text-sm leading-6'>
              Making sure assignments make sense for your situation
            </li>
            <li className='text-sm leading-6'>Using your professional judgment</li>
            <li className='text-sm leading-6'>Adjusting suggestions when needed</li>
          </ul>
          <p className='text-sm leading-6 mt-4'>
            Vehix is a tool to help you, not to make decisions for you. The choices are yours.
          </p>
        </section>

        <section className='mb-20' id='questions'>
          <h2 className='text-3xl font-bold mb-6'>6. Questions?</h2>
          <p className='text-sm leading-6 mb-6'>
            If you have questions about how smart features work, contact us:
          </p>

          <div className='p-6 bg-muted/30 rounded-lg border border-border'>
            <p className='text-sm leading-6'>
              <strong>Kebi AI LLC</strong>
              <br />
              Email: <span className='text-primary'>support@kebi.ai</span>
              <br />
              Privacy questions: <span className='text-primary'>privacy@kebi.ai</span>
            </p>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
};
